/**
 * Hockey Position Recommendation Rules
 * 
 * Defines position recommendations for Hockey players based on their attributes
 */

import type { PositionRule } from '../positionRecommendations';

/**
 * Hockey Position Rules
 */
export const HOCKEY_POSITION_RULES: PositionRule[] = [
  // PREMIUM POSITIONS - Require elevated thresholds
  {
    position: 'G',
    tier: 'premium',
    description: 'Goaltender - Requires positioning, reflexes, and awareness',
    requirements: [
      { attribute: 'positioning', minValue: 7, required: true },
      { attribute: 'awareness', minValue: 7, required: true },
      { attribute: 'agility', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const awareness = attrs.get('awareness') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      const focus = attrs.get('focus') || 0; // Mental attribute
      return positioning * 2.0 + awareness * 1.8 + agility * 1.4 + speed * 1.0 + focus * 1.2;
    },
  },
  {
    position: 'C',
    tier: 'premium',
    description: 'Center - Requires all-around skills, faceoffs, and playmaking',
    requirements: [
      { attribute: 'passing', minValue: 7, required: true },
      { attribute: 'skating', minValue: 7, required: true },
      { attribute: 'awareness', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const passing = attrs.get('passing') || 0;
      const skating = attrs.get('skating') || 0;
      const awareness = attrs.get('awareness') || 0;
      const shooting = attrs.get('shooting') || 0;
      const checking = attrs.get('checking') || 0;
      const sport_iq = attrs.get('sport_iq') || 0;
      return passing * 1.8 + skating * 1.6 + awareness * 1.4 + shooting * 1.2 + checking * 1.1 + sport_iq * 1.2;
    },
  },

  // STANDARD POSITIONS - Moderate thresholds
  {
    position: 'LW',
    tier: 'standard',
    description: 'Left Wing - Requires shooting, skating, and offensive positioning',
    requirements: [
      { attribute: 'shooting', minValue: 6, required: true },
      { attribute: 'skating', minValue: 6, required: true },
      { attribute: 'positioning', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const shooting = attrs.get('shooting') || 0;
      const skating = attrs.get('skating') || 0;
      const positioning = attrs.get('positioning') || 0;
      const passing = attrs.get('passing') || 0;
      const checking = attrs.get('checking') || 0;
      return shooting * 1.8 + skating * 1.6 + positioning * 1.3 + passing * 1.1 + checking * 1.0;
    },
  },
  {
    position: 'RW',
    tier: 'standard',
    description: 'Right Wing - Requires shooting, skating, and offensive positioning',
    requirements: [
      { attribute: 'shooting', minValue: 6, required: true },
      { attribute: 'skating', minValue: 6, required: true },
      { attribute: 'positioning', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const shooting = attrs.get('shooting') || 0;
      const skating = attrs.get('skating') || 0;
      const positioning = attrs.get('positioning') || 0;
      const passing = attrs.get('passing') || 0;
      const checking = attrs.get('checking') || 0;
      return shooting * 1.8 + skating * 1.6 + positioning * 1.3 + passing * 1.1 + checking * 1.0;
    },
  },
  {
    position: 'LD',
    tier: 'standard',
    description: 'Left Defense - Requires defensive positioning, checking, and passing',
    requirements: [
      { attribute: 'positioning', minValue: 6, required: true },
      { attribute: 'checking', minValue: 6, required: true },
      { attribute: 'passing', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const checking = attrs.get('checking') || 0;
      const passing = attrs.get('passing') || 0;
      const skating = attrs.get('skating') || 0;
      const awareness = attrs.get('awareness') || 0;
      const strength = attrs.get('strength') || 0;
      return positioning * 1.7 + checking * 1.6 + passing * 1.3 + skating * 1.2 + awareness * 1.2 + strength * 1.1;
    },
  },
  {
    position: 'RD',
    tier: 'standard',
    description: 'Right Defense - Requires defensive positioning, checking, and passing',
    requirements: [
      { attribute: 'positioning', minValue: 6, required: true },
      { attribute: 'checking', minValue: 6, required: true },
      { attribute: 'passing', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const checking = attrs.get('checking') || 0;
      const passing = attrs.get('passing') || 0;
      const skating = attrs.get('skating') || 0;
      const awareness = attrs.get('awareness') || 0;
      const strength = attrs.get('strength') || 0;
      return positioning * 1.7 + checking * 1.6 + passing * 1.3 + skating * 1.2 + awareness * 1.2 + strength * 1.1;
    },
  },

  // EASIER POSITIONS — accessible for less-developed players, ensuring anyone
  // with attributes recorded always receives at least one recommendation.
  // Defense is the natural home for developing hockey players who skate but
  // haven't built shooting or advanced offensive skills yet.
  {
    position: 'LD',
    tier: 'easier',
    description: 'Left Defense - Accessible for players with basic skating and physical play',
    requirements: [
      { attribute: 'positioning', minValue: 4, required: false },
      { attribute: 'skating', minValue: 4, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const checking = attrs.get('checking') || 0;
      const skating = attrs.get('skating') || 0;
      const strength = attrs.get('strength') || 0;
      return positioning * 1.5 + checking * 1.4 + skating * 1.3 + strength * 1.0;
    },
  },
  {
    position: 'RD',
    tier: 'easier',
    description: 'Right Defense - Accessible for players with basic skating and physical play',
    requirements: [
      { attribute: 'positioning', minValue: 4, required: false },
      { attribute: 'skating', minValue: 4, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const checking = attrs.get('checking') || 0;
      const skating = attrs.get('skating') || 0;
      const strength = attrs.get('strength') || 0;
      return positioning * 1.5 + checking * 1.4 + skating * 1.3 + strength * 1.0;
    },
  },
];

